import { MiniGameBase } from './base.js';

/**
 * "Match the Definition" matching mini-game
 * Player matches terms to their definitions
 * Same mechanic as Who Said It, different labels
 */
export class MatchDefinitionGame extends MiniGameBase {
    constructor(deepDiveData, categoryName, onComplete = null, category = null) {
        super(deepDiveData, categoryName, onComplete, category);
        this.selectedTerm = null;
        this.selectedDefinition = null;
        this.shuffledDefinitions = this.shuffleDefinitions();
    }

    /**
     * Shuffle definition options for display
     */
    shuffleDefinitions() {
        const definitions = this.data.items.map(item => item.answer);
        return this.shuffleArray(definitions);
    }

    /**
     * Render the matching UI
     */
    render(container) {
        super.render(container);

        container.innerHTML = `
            <div class="deepdive-container">
                <div class="instruction">${this.data.instruction || 'Match each term to its definition:'}</div>
                <div class="progress">${this.getProgress()} matched</div>
                <div class="matching-container">
                    <div class="questions-column">
                        ${this.data.items.map((item, idx) => `
                            <div class="question-item" data-index="${idx}">
                                ${item.question}
                            </div>
                        `).join('')}
                    </div>
                    <div class="answers-column">
                        ${this.shuffledDefinitions.map((definition, idx) => `
                            <div class="answer-item" data-answer="${definition}">
                                ${definition}
                            </div>
                        `).join('')}
                    </div>
                </div>
                <div class="button-row"></div>
            </div>
        `;

        // Add skip button
        const buttonRow = container.querySelector('.button-row');
        const skipBtn = this.createSkipButton();
        buttonRow.appendChild(skipBtn);

        this.attachEventListeners();
        this.updateUI();
    }

    /**
     * Attach click listeners to items
     */
    attachEventListeners() {
        const terms = this.container.querySelectorAll('.question-item');
        const definitions = this.container.querySelectorAll('.answer-item');

        terms.forEach((el, idx) => {
            el.addEventListener('click', () => this.handleTermClick(idx));
        });

        definitions.forEach(el => {
            const definition = el.getAttribute('data-answer');
            el.addEventListener('click', () => {
                this.handleDefinitionClick(definition);
            });
            // Attach hint if this answer matches a word in category
            this.attachHintIfExists(el, definition);
        });
    }

    /**
     * Handle term click
     */
    handleTermClick(termIndex) {
        // Don't allow selecting already matched terms
        if (this.completedItems.includes(termIndex)) {
            return { action: 'already_matched' };
        }

        this.playSelectSound();
        this.selectedTerm = termIndex;
        this.selectedDefinition = null;
        this.updateUI();
        return { action: 'select_term' };
    }

    /**
     * Handle definition click
     */
    handleDefinitionClick(definition) {
        // Check if this definition is already matched
        const definitionAlreadyMatched = this.data.items.some((item, idx) =>
            this.completedItems.includes(idx) && item.answer === definition
        );

        if (definitionAlreadyMatched) {
            return { action: 'already_matched' };
        }

        // Must select term first
        if (this.selectedTerm === null) {
            return { action: 'select_term_first' };
        }

        this.selectedDefinition = definition;

        // Check if match is correct
        const correctDefinition = this.data.items[this.selectedTerm].answer;

        if (definition === correctDefinition) {
            // Correct match!
            this.playMatchSound();
            this.completedItems.push(this.selectedTerm);
            this.selectedTerm = null;
            this.selectedDefinition = null;
            this.updateUI();
            this.updateProgress();

            if (this.isComplete()) {
                setTimeout(() => this.showCelebration(), 500);
            }

            return { action: 'correct_match' };
        } else {
            // Incorrect match
            this.playErrorSound();
            this.showError();
            setTimeout(() => {
                this.selectedTerm = null;
                this.selectedDefinition = null;
                this.updateUI();
            }, 800);
            return { action: 'incorrect_match' };
        }
    }

    /**
     * Handle generic input (for testing)
     */
    handleInput(selection) {
        if ('term' in selection) {
            return this.handleTermClick(selection.term);
        } else if ('definition' in selection) {
            return this.handleDefinitionClick(selection.definition);
        }
        return { action: 'none' };
    }

    /**
     * Update UI based on current state
     */
    updateUI() {
        if (!this.container) return;

        // Update terms
        const terms = this.container.querySelectorAll('.question-item');
        terms.forEach((el, idx) => {
            el.classList.remove('selected', 'matched');

            if (this.completedItems.includes(idx)) {
                el.classList.add('matched');
            } else if (this.selectedTerm === idx) {
                el.classList.add('selected');
            }
        });

        // Update definitions
        const definitions = this.container.querySelectorAll('.answer-item');
        definitions.forEach(el => {
            const definition = el.getAttribute('data-answer');
            el.classList.remove('selected', 'matched');

            // Check if this definition has been matched
            const matchedIdx = this.data.items.findIndex((item, idx) =>
                this.completedItems.includes(idx) && item.answer === definition
            );

            if (matchedIdx !== -1) {
                el.classList.add('matched');
            } else if (this.selectedDefinition === definition) {
                el.classList.add('selected');
            }
        });
    }

    /**
     * Update progress display
     */
    updateProgress() {
        const progressEl = this.container?.querySelector('.progress');
        if (progressEl) {
            progressEl.textContent = `${this.getProgress()} matched`;
        }
    }

    /**
     * Show error animation
     */
    showError() {
        const terms = this.container.querySelectorAll('.question-item');
        const definitions = this.container.querySelectorAll('.answer-item');

        if (this.selectedTerm !== null) {
            terms[this.selectedTerm]?.classList.add('error');
            setTimeout(() => {
                terms[this.selectedTerm]?.classList.remove('error');
            }, 300);
        }

        definitions.forEach(el => {
            if (el.getAttribute('data-answer') === this.selectedDefinition) {
                el.classList.add('error');
                setTimeout(() => el.classList.remove('error'), 300);
            }
        });
    }

    /**
     * Check if game is complete
     */
    isComplete() {
        return this.completedItems.length === this.data.items.length;
    }

    /**
     * Show celebration
     */
    showCelebration() {
        super.showCelebration();
        // Could add more elaborate celebration here
    }
}
